import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useRemainingPacks } from '../hooks/useRemainingPacks';
import { useRemainingCalculations } from '../hooks/useRemainingCalculations';

// Define pro user state
interface ProUserState {
	isProUser: boolean;
	loading: boolean;
	error: string | null;
}

// Define actions
type ProUserAction =
	| { type: 'SET_PRO_STATUS'; payload: boolean }
	| { type: 'SET_LOADING'; payload: boolean }
	| { type: 'SET_ERROR'; payload: string | null }
	| { type: 'REFRESH' };

// Initial state
const initialState: ProUserState = {
	isProUser: false,
	loading: false,
	error: null,
};

// Reducer function
const proUserReducer = (state: ProUserState, action: ProUserAction): ProUserState => {
	switch (action.type) {
		case 'SET_PRO_STATUS':
			return { ...state, isProUser: action.payload };
		case 'SET_LOADING':
			return { ...state, loading: action.payload };
		case 'SET_ERROR':
			return { ...state, error: action.payload };
		case 'REFRESH':
			return { ...state, loading: true };
		default:
			return state;
	}
};

// Create context
const ProUserContext = createContext<
	| {
			state: ProUserState;
			dispatch: React.Dispatch<ProUserAction>;
			refetchProStatus: () => void;
	  }
	| undefined
>(undefined);

// ProUserProvider Component
export const ProUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [state, dispatch] = useReducer(proUserReducer, initialState);
	// alert('ProUserProvider');

	const {
		data: remainingPacks,
		refetch,
		loading,
		error,
	} = useRemainingPacks(process.env.REACT_APP_PRICE_COMPARATOR_ID || '354');

	const {
		data: remainingCalcs,
		loading: loadingCalcs,
		error: errorCalcs,
		refetch: refetchCalcs,
	} = useRemainingCalculations(process.env.REACT_APP_PRICE_CALCULATOR_ID || '353');

	// console.log('🔍 ProUserContext - remainingCalcs:', remainingCalcs);
	// console.log('🔍 ProUserContext - remainingPacks:', remainingPacks);

	// Calcular si el usuario es pro basado en los packs disponibles
	useEffect(() => {
		dispatch({ type: 'SET_LOADING', payload: loading });
		dispatch({ type: 'SET_ERROR', payload: error });

		if (
			(!remainingPacks?.results || remainingPacks.results.length === 0) &&
			(!remainingCalcs?.remaining || remainingCalcs.remaining === 0)
		) {
			dispatch({ type: 'SET_PRO_STATUS', payload: false });
			return;
		}

		// console.log('🔍 ProUserContext - remainingCalcs:', remainingCalcs);

		const hasProPack = remainingPacks?.results.some((pack) => pack.remainingUses >= 1);
		const filteredPacks = remainingPacks?.results.filter((pack) => pack.remainingUses >= 1);

		// Si tiene remaining calculations mayor a 0, también considerar como pro user
		const hasRemainingCalculations = Boolean(remainingCalcs?.remaining && remainingCalcs.remaining > 0);

		// El usuario es pro si tiene packs disponibles O si tiene cálculos restantes
		const isProUser = hasProPack || hasRemainingCalculations;

		dispatch({ type: 'SET_PRO_STATUS', payload: isProUser });

		// console.log('🔍 ProUserContext - isProUser:', isProUser, 'Packs:', remainingPacks.results);
		// console.log('🔍 ProUserContext - Paquetes filtrados (remainingUses >= 1):', filteredPacks);
		// console.log('🔍 ProUserContext - hasRemainingCalculations:', hasRemainingCalculations);
	}, [remainingPacks, loading, error, remainingCalcs, loadingCalcs, errorCalcs]);

	const refetchProStatus = () => {
		// console.log('🔄 ProUserContext - Refreshing pro status');
		dispatch({ type: 'REFRESH' });
		refetch();
		refetchCalcs();
	};

	const value = {
		state,
		dispatch,
		refetchProStatus,
	};

	return <ProUserContext.Provider value={value}>{children}</ProUserContext.Provider>;
};

// Custom hook for using pro user context
export const useProUser = () => {
	const context = useContext(ProUserContext);
	if (!context) {
		throw new Error('useProUser must be used within a ProUserProvider');
	}
	return {
		isProUser: context.state.isProUser,
		loading: context.state.loading,
		error: context.state.error,
		refetchProStatus: context.refetchProStatus,
	};
};
